import {useRef} from 'react';

export interface UseStableCyclicalOpenVersionIdArgs {
  templateId: string;
  openVersionApiId: string | undefined;
  isLatest: boolean;
  isViewingPublishedVersion: boolean;
}

interface CyclicalOpenVersionNavigationContext {
  templateId: string;
  isLatest: boolean;
  isViewingPublishedVersion: boolean;
}

/**
 * Preserves the last known publishable version api id across transient gaps where
 * VersionedItemSubstore clears openVersionApiId (e.g. during autosave). Resets when
 * template or publishing navigation context changes so cyclical review UI does not
 * reuse a version id from a different template or published/draft transition.
 */
export function useStableCyclicalOpenVersionId({
  templateId,
  openVersionApiId,
  isLatest,
  isViewingPublishedVersion,
}: UseStableCyclicalOpenVersionIdArgs): string | undefined {
  const lastOpenVersionApiIdRef = useRef<string | undefined>();
  const navigationContextRef = useRef<CyclicalOpenVersionNavigationContext>();

  const didNavigationContextChange =
    templateId !== navigationContextRef.current?.templateId ||
    isLatest !== navigationContextRef.current?.isLatest ||
    isViewingPublishedVersion !== navigationContextRef.current?.isViewingPublishedVersion;

  if (didNavigationContextChange) {
    lastOpenVersionApiIdRef.current = undefined;
    navigationContextRef.current = {templateId, isLatest, isViewingPublishedVersion};
  }

  if (openVersionApiId != null) {
    lastOpenVersionApiIdRef.current = openVersionApiId;
  }

  return openVersionApiId ?? lastOpenVersionApiIdRef.current;
}
